import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import { Tooltip, OverlayTrigger, Card, Row, Col } from 'react-bootstrap-5';
import { Link } from 'react-router-dom';
import { Button } from 'components/ui/button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard, faAngleDoubleRight, faPlus, faCheck } from '@fortawesome/pro-solid-svg-icons';

import { showLogTaskModal, showEditTaskModal } from 'bundles/project/actions/index';

import LoadingSpinner from 'components/ui/loading_spinner';
import SelectFilter from 'components/search_sidebar/select_filter';
import SearchPage from 'components/search_page';
import FilterTopbar from 'components/search_page/filter_topbar';
import FilterRow from 'components/search_page/filter_row';
import taskPropTypes from 'components/tasks/prop_types';
import { parseDate } from 'helpers/date_utils';
import ActivityCard from 'components/project/activity_card';
import EmptyState from 'components/project/empty_state';
import { filter, isEqual, sortBy, sortedUniqBy } from 'lodash';

const filterOptions = {
  type: [
    {
      value: '',
      label: 'All Types'
    },
    {
      value: 'call',
      label: 'Call'
    },
    {
      value: 'email',
      label: 'Email'
    },
    {
      value: 'meeting',
      label: 'Meeting'
    },
    {
      value: 'note',
      label: 'Note'
    },
    {
      value: 'other',
      label: 'Other'
    },
    {
      value: 'showing',
      label: 'Showing'
    },
    {
      value: 'critical_date',
      label: 'Critical Date'
    }
  ]
};
const resultsPerPage = 20;

export class ActivityFeed extends React.Component {
  static propTypes = {
    completedTasks: PropTypes.arrayOf(taskPropTypes).isRequired,
    fetching: PropTypes.bool.isRequired,
    forwardingEmail: PropTypes.string,
    limitView: PropTypes.bool.isRequired,
    linkToActivitiesTab: PropTypes.bool,
    showEditTaskModal: PropTypes.func,
    showLogTaskModal: PropTypes.func
  };

  static defaultProps = {
    limitView: false
  };

  state = {
    copiedForwardingEmail: false,
    limit: resultsPerPage,
    filters: {
      user: null,
      type: null,
      contact: null
    }
  }

  getContactOptions = () => {
    const options = [];

    this.props.completedTasks.forEach((task) => {
      const contactIds = options.map(o => o.value.id);
      const contacts = [];

      task.associatedModels.forEach((model) => {
        if (model.associatedModelType.includes("Contact") && !contactIds.includes(model.associatedModelId)) {
          contacts.push(model);
        }
      });

      const mappedOptions = contacts.map(contact => ({
        value: {
          id: contact.associatedModelId,
          type: contact.associatedModelType
        },
        label: contact.name
      }));

      options.push(...mappedOptions);
    });

    return sortBy(options, o => o.label.toUpperCase());
  }

  incrementDisplayLimit = (e) => {
    e.preventDefault();
    this.setState((state) => {
      const limit = state.limit + resultsPerPage;
      return { limit };
    });
  }

  filterOptions() {
    const users = this.props.completedTasks.map(t => ({ value: t.assigneeId, label: t.assignee }));

    const filters = {
      ...filterOptions,
      user: [
        { value: '', label: 'All Users' },
        ...sortedUniqBy(sortBy(users, 'value'), 'value')
      ]
    };

    const contacts = this.getContactOptions();

    if (contacts.length > 0) filters.contact = [{ value: '', label: 'All Contacts' }, ...contacts];

    return filters;
  }

  copyEmailToClipboard = () => {
    this.setState({ copiedForwardingEmail: true });
    navigator.clipboard.writeText(this.props.forwardingEmail);
    setTimeout(() => {
      this.setState({ copiedForwardingEmail: false });
    }, 2000);
  }

  showModal = (e) => {
    e.preventDefault();
    this.props.showLogTaskModal();
  }

  userFilter = (tasks) => {
    if (!this.state.filters.user) return tasks;

    return filter(tasks, task => this.state.filters.user === task.assigneeId);
  }

  typeFilter = (tasks) => {
    if (!this.state.filters.type) return tasks;

    return filter(tasks, task => this.state.filters.type === task.taskType);
  }

  contactFilter = (tasks) => {
    if (!this.state.filters.contact) return tasks;

    return filter(tasks, (task) => {
      const associatedModels = task.associatedModels.map(model => ({
        id: model.associatedModelId,
        type: model.associatedModelType
      }));

      return !!associatedModels.find(model => isEqual(model, this.state.filters.contact));
    });
  }

  filteredTasks() {
    let tasks = this.props.completedTasks;
    tasks = this.userFilter(tasks);
    tasks = this.typeFilter(tasks);
    tasks = this.contactFilter(tasks);

    return tasks.slice(0, this.state.limit) || [];
  }

  currentFilterValue = (filters, key) => filters[key].find(option => option.value === this.state.filters[key])

  updateFilter = (e, key) => {
    this.setState((prevState) => {
      const filters = { ...prevState.filters };
      filters[key] = e ? e.value : null;
      return { filters };
    });
  }

  showForwardingEmail = () => {
    return this.props.forwardingEmail && !this.props.limitView;
  }

  renderSeeMoreLink() {
    if (this.props.limitView || this.state.limit >= this.props.completedTasks.length) return;

    return (
      <a href="#" onClick={this.incrementDisplayLimit}>
        Show more...
      </a>
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderHeader()}
        {this.renderFilterTopbar()}
        {this.renderActivities()}
        {this.renderSeeMoreLink()}
      </React.Fragment>
    );
  }

  renderHeader() {
    const activityCount = pluralize('logged activity', this.props.completedTasks.length, true);

    const subtitle = this.props.limitView && !this.props.fetching ? ` - ${activityCount}` : null;

    return (
      <React.Fragment>
        {this.showForwardingEmail() &&
          <div className="text-end mb-2" onClick={this.copyEmailToClipboard}>
            project forwarding email:
            <span className="text-muted mx-2" id="projectForwardingEmail">
              {this.props.forwardingEmail}
            </span>
            {this.renderClipboardIcon()}
          </div>
        }
        <div className={this.props.limitView ? 'pt-5 my-2' : ''}>
          <Row className="mb-2">
            <Col>
              {this.props.limitView && (
                <div className="fs-large fw-semibold">
                  Activity Feed <span className="text-muted">{subtitle}</span>
                </div>
              )}
              {!this.props.limitView && (
                <h5>
                  Activity Feed <span className="text-muted">{subtitle}</span>
                </h5>
              )}
            </Col>
            <Col xs="auto">
              <div className="d-flex gap-4 align-items-center">
                {this.renderActions()}
              </div>
            </Col>
          </Row>
        </div>
      </React.Fragment>
    );
  }

  renderFilterTopbar() {
    if (this.props.limitView || this.props.fetching) return;

    const completedTasks = this.filteredTasks();
    const pageData = {
      currentPage: 1,
      itemCount: completedTasks.length,
      itemSingularLabel: 'activity',
      pageSize: resultsPerPage,
      totalCount: completedTasks.length
    };
    const filters = this.filterOptions();

    return (
      <SearchPage>
        <FilterTopbar className="w-100 p-0 border-0 mb-3">
          <FilterRow pageData={pageData}>
            {filters.contact && (
              <SelectFilter
                defaultValue={filters.contact[0]}
                name="contact-filter"
                options={filters.contact}
                value={this.currentFilterValue(filters, 'contact')}
                onChange={e => this.updateFilter(e, 'contact')}
              />
            )}
            {filters.user && (
              <div className={filters.contact && 'left-buffer-md'}>
                <SelectFilter
                  defaultValue={filters.user[0]}
                  name="name-filter"
                  options={filters.user}
                  value={this.currentFilterValue(filters, 'user')}
                  onChange={e => this.updateFilter(e, 'user')}
                />
              </div>
            )}
            <div className={(filters.contact || filters.user) && 'left-buffer-md'}>
              <SelectFilter
                defaultValue={filters.type[0]}
                name="type-filter"
                options={filters.type}
                value={this.currentFilterValue(filters, 'type')}
                onChange={e => this.updateFilter(e, 'type')}
              />
            </div>
          </FilterRow>
        </FilterTopbar>
      </SearchPage>
    );
  }


  renderActions() {
    if (this.props.limitView) {
      return (
        <div className="cta-links d-flex flex-nowrap gap-4">
          {!this.props.fetching && (
            <a className="btn-link" href="#" name="logActivity" onClick={this.showModal}>
              <FontAwesomeIcon icon={faPlus} size="sm" /> Activity
            </a>
          )}

          {this.props.linkToActivitiesTab && (
            <Link className="ml-5 btn-link" href="#" name="goToActivityFeed" to="/activities">
              <FontAwesomeIcon icon={faAngleDoubleRight} /> More
            </Link>
          )}
        </div>
      );
    }

    if (!this.props.fetching) {
      return (
        <Button label="New Activity" name="logActivity" onClick={this.showModal} />
      );
    }
  }

  renderClipboardIcon() {
    if (this.state.copiedForwardingEmail) {
      return (
        <span className="text-success">
          <FontAwesomeIcon className="right-buffer-sm" icon={faCheck}/>
          Copied
        </span>
      );
    }
    const tooltip = (
      <Tooltip id="copyToClipboard">Copy forwarding email</Tooltip>
    );
    return (
      <OverlayTrigger overlay={tooltip} placement="top">
        <FontAwesomeIcon icon={faClipboard}/>
      </OverlayTrigger>
    );
  }

  renderEmptyState() {
    const noActivityOnProject = this.props.completedTasks.length === 0;
    const showSubMessage = !this.props.limitView && noActivityOnProject;

    return (
      <Card>
        <Card.Body className="mt-2 p-0">
          <EmptyState
            imageSrc="/images/project_empty_states/activity.svg"
            message={`No Activity ${noActivityOnProject ? 'to display.' : 'matches the current search crtieria.'}`}
            subMessage={showSubMessage ? 'Get started by clicking on the “New Activity” button.' : null}
          />
        </Card.Body>
      </Card>
    );
  }

  renderActivities() {
    const { fetching, limitView } = this.props;

    if (fetching) {
      return (
        <div className="text-center">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    const completedTasks = this.filteredTasks();

    if (completedTasks.length === 0) return this.renderEmptyState();

    return (
      <div className="d-flex flex-column gap-2">
        {(limitView ? completedTasks.slice(0, 5) : completedTasks).map(task => (
          <ActivityCard
            activityType={task.taskType}
            associatedModels={task.associatedModels.filter(model => model.associatedModelType.includes('Contacts'))}
            author={task.assignee}
            authorImageUrl={task.assigneeImageUrl}
            authorInitials={task.assigneeInitials}
            body={task.body}
            hasCrmEmail={task.hasCrmEmail}
            key={task.id}
            pinned={task.pinned}
            timestamp={task.completedAt}
            title={task.title}
            onClick={() => this.props.showEditTaskModal(task)}
          />
        ))}
      </div>
    );
  }
}

const sortTasks = (a, b) => b.pinned - a.pinned || parseDate(b.completedAt) - parseDate(a.completedAt);

const mapStateToProps = ({ tasks, project }) => ({
  completedTasks: tasks.fetching ? [] : tasks.filter(task => task.completed).sort(sortTasks),
  fetching: !!tasks.fetching,
  forwardingEmail: project.forwardingEmail
});

const mapDispatchToProps = { showLogTaskModal, showEditTaskModal };

export default connect(mapStateToProps, mapDispatchToProps)(ActivityFeed);
